import React from 'react'
import { Navigate } from 'react-router-dom'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { moduleState, useAccess } from '../../lib/access'
import styles from './ModulePreview.module.css'

// The unlicensed Stock & Dispatch page (handoff §7o.2; D2, Kerrin
// 2026-10-01). A PackRight workspace without the licence has this one page
// in place of the module, for its ADMIN and SYSADMIN only (lib/access.ts
// STOCK_PREVIEW; anyone else is refused by the module's own read). It is a
// description, never a module: no data, no screenshots of an empty module,
// no disabled controls. Its one action opens an email draft to Dataphyll in
// the reader's own mail client; nothing is sent from here.

export const ENQUIRY_ADDRESS = 'support@dataphyll.com'

const BODY = [
  'Stock and dispatch turns received fruit into pallets, customer orders and verified truck loads, without a second system or a spreadsheet.',
  'Stock arrives from your existing picking and packhouse receipts. You reserve boxes against a customer order, plan and confirm pallets, load a truck and verify every pallet before it leaves. Only a confirmed departure moves your stock on hand.',
  'It also carries customer and destination records, pallet and export label printing, delivery paperwork, and draft invoices into Xero for what actually left.',
]

const POINTS: [string, string][] = [
  ['One stock picture', 'On hand, held, reserved and available boxes per harvest source. Never merged across harvest date, block, contractor or pack type.'],
  ['Orders that need approving', 'Draft, approve, and reapprove when something material changes. Preparation and customer labels wait for approval.'],
  ['Pallets and loads', 'Plan pallets at the job’s capacity, confirm what was actually built, verify each pallet onto a truck by identity, not by count.'],
  ['Paperwork and invoicing', 'Customer and export labels, dockets and packing lists, and draft invoices in Xero for dispatched quantities.'],
]

// The draft: the tenant it is about, and the three questions a buyer asks
// (cost, what setup needs from them, how long it takes).
export function enquiryHref(workspace: { id: string; name: string }) {
  const subject = `Stock & Dispatch for ${workspace.name}`
  const body = [
    'Hello Dataphyll,',
    '',
    `We would like to know more about the Stock & Dispatch module for ${workspace.name}:`,
    '',
    '- What does it cost?',
    '- What does setting it up need from us?',
    '- How long does setting it up take?',
    '',
    `Workspace: ${workspace.name}`,
    `Workspace ID: ${workspace.id}`,
    '',
    'Thanks,',
    '',
  ].join('\n')
  return `mailto:${ENQUIRY_ADDRESS}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

export const ModulePreviewPage: React.FC = () => {
  const access = useAccess()
  const { currentWorkspace } = useWorkspace()
  // A licensed workspace has the module itself, not its description.
  if (access && moduleState(access) === 'licensed') return <Navigate to="/stock-dispatch/overview" replace />
  const workspace = { id: currentWorkspace?.id || '', name: currentWorkspace?.name || 'our workspace' }

  return (
    <div className={styles.scroll}>
      <div className={styles.body}>
        <div className={styles.banner} role="note" data-testid="preview-banner">
          <svg className={styles.lock} width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="3" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <div className={styles.bannerText}>
            <span className={styles.bannerTitle}>Your subscription does not include this module</span>
            <span className={styles.bannerLine}>
              Stock & Dispatch is an optional extra on top of Control. Everything below describes what it would do; this page is a preview, and there is no
              stock, order or dispatch data behind it.
            </span>
          </div>
        </div>

        <div>
          <div className={styles.kickerRow}>
            <span className={styles.kicker}>Optional module</span>
            <span className={styles.pill}>Preview only</span>
          </div>
          <h1 className={styles.title}>Stock & Dispatch</h1>
        </div>

        <div className={styles.paras}>
          {BODY.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>

        <div className={styles.cards} aria-label="What the module does">
          {POINTS.map(([k, v]) => (
            <div key={k} className={styles.card} data-testid="preview-card">
              <div className={styles.cardTitle}>{k}</div>
              <p>{v}</p>
            </div>
          ))}
        </div>

        <p className={styles.note}>Your data stays as it is. Nothing about how you record hours, harvest or wages changes when the module is added.</p>

        <div className={styles.actions}>
          <a className={styles.cta} href={enquiryHref(workspace)}>
            Email us about this module
          </a>
        </div>
      </div>
    </div>
  )
}
